<template>
  <ViewHeader :title="matchedItem?.title" :dynamic-breadcrumbs="breadcrumbs" />

  <Block v-if="countriesApi.loading.value">
    <Spinner color="primary" size="large" />
  </Block>
  <Block v-else-if="error">
    <Notification type="error" :text="$t(error)">
      <template #actions>
        <Button
          :text="$t('tryAgain')"
          @click="getCountries"
          size="small"
          color="error"
        >
          <template #icon>
            <i-mdi-refresh />
          </template>
        </Button>
      </template>
    </Notification>
  </Block>

  <Block v-else-if="route.name === 'countryContainer' && countries.length">
    <List>
      <ListItem
        v-for="item of countries"
        :key="item.id"
        :text="item.title"
        :footer="$t('numStations', item.num_stations)"
        :to="{ path: `${router.currentRoute.value.path}/${item.name}` }"
      >
        <template #icon>
          <i-mdi-cube />
        </template>
      </ListItem>
    </List>
  </Block>

  <Block v-else-if="matchedItem">
    <SearchResults :country="matchedItem.name" :show-country="false" />
  </Block>
</template>

<script setup lang="ts">
import type { Country, CountriesResponse } from '@/types'
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useApi } from '@/lib/api'
import { useDynamicRoute } from '@/lib/dynamic-route'

const route = useRoute()
const router = useRouter()

const countriesApi = useApi()
const countries = ref<Country[]>([])
const error = ref<string | undefined>(undefined)

const { matchedItem, breadcrumbs } = useDynamicRoute({
  level: 3,
  _children: countries,
  onSlugMismatch: () => {
    router.replace({ name: 'error404' })
  },
})

const getCountries = async () => {
  try {
    error.value = undefined

    const response = await countriesApi.ky
      .get('countries/')
      .json<CountriesResponse>()

    if (!response?.data.length) {
      error.value = 'countries.resolveError'
      return
    }

    countries.value = response.data
  } catch (_e) {
    error.value = 'countries.resolveError'
    countries.value = []
  }
}

onMounted(() => {
  getCountries()
})
</script>
